import {
  applyActionCode,
  confirmPasswordReset,
  signOut,
  getIdToken,
  signInWithEmailAndPassword,
  sendPasswordResetEmail,
} from 'firebase/auth'
import { QueryCache } from 'react-query'
import { GraphQLClient } from 'graphql-request'

import { ResetPassword, SignIn } from '../types/sign-in'
import { User, UserIdTokenResult } from '../types/user'

import { auth } from '../utils/firebase'
import { queryClient } from '../utils/react-query'
import userQueries from './graphql/users/queries'

const middleware = async (response: any) => {
  if (
    response?.response?.errors &&
    response?.response?.errors?.some((error: any) => error?.extensions?.code === 'auth/id-token-expired')
  ) {
    if (auth.currentUser) {
      const access_token = await getIdToken(auth.currentUser, true)

      authService.setSession(access_token)
    }
  }
}

export const graphQLClient = new GraphQLClient(process.env.REACT_APP_PRISMA as string, {
  responseMiddleware: middleware,
})

class AuthService {
  setSession = (accessToken?: string) => {
    if (accessToken) {
      graphQLClient.setHeader('authorization', `Bearer ${accessToken}`)
    } else {
      graphQLClient.setHeader('authorization', '')
    }
  }

  getUser = async (id?: string): Promise<any> => {
    let userId = id

    if (!id) {
      userId = await auth.currentUser?.uid
    }

    if (!userId) {
      throw Error('User not logged in')
    }

    const { me: user } = await graphQLClient.request<{ me: User }>(userQueries.getMe)

    if (!user) {
      throw Error('User not logged in')
    }

    return user
  }

  requestPasswordReset = async (data: ResetPassword) => {
    await sendPasswordResetEmail(auth, data.email)
  }
  logout = async () => {
    await signOut(auth)

    const queryCache = new QueryCache()
    queryCache.clear()
    await queryClient.resetQueries()
    this.setSession()
  }

  loginWithCredentials = async (data: SignIn) => {
    await signInWithEmailAndPassword(auth, data.email, data.password)
  }

  getAccessToken = async (): Promise<string | undefined> => await auth.currentUser?.getIdToken(true)

  getAccessTokenResults = async (): Promise<UserIdTokenResult | undefined> => {
    const result = await auth.currentUser?.getIdTokenResult()

    return result as UserIdTokenResult | undefined
  }

  isAuthenticated = async () => !!(await this.getAccessToken())

  verifyEmail = async (oobCode: string): Promise<void> => {
    return await applyActionCode(auth, oobCode)
  }

  resetPassword = async (data: { oobCode: string; newPassword: string }) => {
    return await confirmPasswordReset(auth, data.oobCode, data.newPassword)
  }
}

const authService = new AuthService()

export default authService
